import { RemoteSensingApi } from '@core/api';
import { CursorDTO } from '@core/models';
import { useQuery } from '@tanstack/react-query';
import dayjs, { DATE_FORMATS } from '@utils/dateUtils';
import type { AxiosError } from 'axios';
import type {
  AoisNotification,
  DownloadMetadata,
  DownloadNotification,
  DroneUploadMetadata,
  DroneUploadNotification,
} from './useDownloadNotifications.models';
import { useReadAoisNotification } from './useReadAoisNotification';

type RawDownloadNotification = Omit<DownloadNotification, 'metadata'> & {
  metadata: DownloadMetadata;
};
type RawDroneUploadNotification = Omit<DroneUploadNotification, 'metadata'> & {
  metadata: DroneUploadMetadata;
};
type RawAoisNotification = RawDownloadNotification | RawDroneUploadNotification;

function useAoisNotifications({ propertyId }: { readonly propertyId?: string }) {
  const { mutate } = useReadAoisNotification();

  const query = useQuery({
    queryKey: ['aois-notifications', { propertyId }],
    queryFn: async (): Promise<{ [key: string]: AoisNotification }> => {
      try {
        const { data } = await RemoteSensingApi.get<CursorDTO<RawAoisNotification>>(
          '/aois/notifications',
          {
            params: {
              start_date: dayjs().subtract(7, 'd').format(DATE_FORMATS.USA_FORMAT),
              aoi_type: 'property',
              uid: propertyId,
              metadata: true,
              sources: 'all',
            },
          }
        );

        return data.content.reduce<{ [key: string]: AoisNotification }>((acc, cur) => {
          const read = !!localStorage.getItem(`image_catalog_file_${cur.id}`);
          acc[cur.id] = { ...cur, metadata: { ...cur.metadata, read } } as AoisNotification;
          return acc;
        }, {});
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError?.response?.status === 404) {
          return {};
        }
        throw error;
      }
    },
    refetchInterval: 25000,
    enabled: !!propertyId,
  });

  // eslint-disable-next-line @tanstack/query/no-rest-destructuring -- full query object re-exposed by design, matching sibling notification hooks.
  return { ...query, readNotifications: mutate };
}

export { useAoisNotifications };
